/**
 * OrderEditor — editor completo de CockpitOrder.
 * Layout:
 *   - Header: nº, status badge, lock badge, action buttons condicionais ao estado
 *   - Customer panel
 *   - Detalhes (datas, compromisso, transporte, anexo VII)
 *   - Linhas (read-only exceto DRAFT/BLOCKED)
 *   - Totais footer
 *   - Histórico
 */
import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useOrder } from '../../hooks/useOrder.js';
import { useOrderLock } from '../../hooks/useOrderLock.js';
import { useTableProfile } from '../../hooks/useTableProfile.js';
import api from '../../services/api.js';
import OrderStatusBadge, { STATUS_LABELS } from './components/OrderStatusBadge.jsx';
import LockBadge from './components/LockBadge.jsx';
import BlockOrderModal from './components/BlockOrderModal.jsx';
import { DataTable } from '@shared/UI/DataTable';
import '@shared/UI/DataTable/DataTable.css';

const STATES = { DRAFT:1, BLOCKED:2, DELIVERED:3, DONE:4, CANCELED:5, AVAILABLE:6,
  WAITING_HOSPITALAR_RESOLUTION:8, RELEASED_BY_HOSPITALAR:9, WAITING_EXCEPTION_APPROVAL:10,
  NEEDS_CLIENT_REVIEW:11 };

const eur = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const date = (s) => s ? new Date(s).toLocaleDateString('pt-PT') : '';

const OrderEditor = ({ id }) => {
  const { order, loading, error, refresh } = useOrder(id);
  const lock = useOrderLock(id, { enabled: order && ![STATES.DONE, STATES.CANCELED].includes(order.status_id) });
  const [blockModal, setBlockModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_order_editor_lines');

  const columns = useMemo(() => [
    { key: 'line_number', label: 'Ln', width: 60 },
    { key: 'input_code', label: 'Código', width: 110, render: (l) => l.input_code || '—' },
    { key: 'quantity', label: 'Qty', width: 70, aggregate: 'sum' },
    { key: 'catalog_sap', label: 'SAP', width: 110, render: (l) => <code>{l.catalog_sap || '—'}</code> },
    { key: 'catalog_nome', label: 'Produto', width: 200, render: (l) => l.catalog_nome || '—' },
    { key: 'marca', label: 'Marca', width: 110, render: (l) => l.marca || '—' },
    { key: 'use_ocl', label: 'OCL', width: 60, render: (l) => l.use_ocl ? '✓' : '' },
    { key: 'is_oferta_k007', label: 'K007', width: 60, render: (l) => l.is_oferta_k007 ? '🎁' : '' },
    { key: 'unit_price', label: 'Preço', width: 100, aggregate: 'sum', render: (l) => eur(l.unit_price) },
    { key: 'line_total', label: 'Total', width: 110, aggregate: 'sum', render: (l) => eur(l.line_total) },
    { key: 'sap_status', label: 'SAP Status', width: 120, render: (l) => l.sap_status ? (
      <span className={`gencrm_badge gencrm_badge--${
        l.sap_status === 'completed' ? 'green' :
        l.sap_status === 'rejected' ? 'red' :
        l.sap_status === 'cancelled' ? 'gray' : 'orange'
      }`}>{l.sap_status}</span>
    ) : null },
    { key: 'pendente', label: 'Pendente', width: 110, render: (l) => (
      <>
        {l.is_pending && <span className="gencrm_badge gencrm_badge--orange">pendente</span>}
        {l.cancelled_at && <span className="gencrm_badge gencrm_badge--gray">cancelada</span>}
      </>
    ) },
  ], []);

  if (loading) return <div className="gencrm_ws_loading">A carregar encomenda…</div>;
  if (error) return <div className="gencrm_ws_error">Erro: {error}</div>;
  if (!order) return <div className="gencrm_ws_empty">Encomenda não encontrada.</div>;

  const isLocked = ![STATES.DONE, STATES.CANCELED].includes(order.status_id);
  const canEdit = [STATES.DRAFT, STATES.BLOCKED].includes(order.status_id);
  const isLockedByOther = lock.locked && !lock.isMine && canEdit;

  // Ações SAP
  const sendToSap = async () => {
    setBusy(true);
    try {
      // 1. Verifica mínimo (pode adicionar transporte)
      const min = await api.get(`/orders/${id}/sap/check-minimum`);
      if (min.data.requires_transport_fee_confirmation) {
        if (!window.confirm(`Total abaixo do mínimo (${eur(min.data.minimum_value)}). Adicionar transporte ${eur(min.data.transport_cost)}?`)) {
          setBusy(false); return;
        }
        await api.post(`/orders/${id}/sap/add-transport-fee`);
      }
      // 2. Send
      const r = await api.post(`/orders/${id}/sap/send`);
      if (!r.data.ok) {
        toast.error(`SAP rejeitou: ${r.data.returns?.[0]?.MESSAGE || 'erro'}`);
      } else {
        toast.success(`Encomenda enviada para SAP! VBELN=${r.data.sales_document}`);
      }
      refresh();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const checkSapStatus = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/orders/${id}/sap/check-status`);
      if (r.data.allCompleted) {
        toast.success('Encomenda finalizada automaticamente — todos itens concluídos.');
      } else {
        const completed = (r.data.status_info || []).filter((i) => i.BILL_DOC).length;
        const pending = (r.data.status_info || []).filter((i) => !i.BILL_DOC && !i.REJ_REASON).length;
        const rejected = (r.data.status_info || []).filter((i) => i.REJ_REASON).length;
        toast.info(`Status: ${completed} concluídos, ${pending} pendentes, ${rejected} rejeitados.`);
      }
      refresh();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const requestPriceResolution = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/orders/${id}/price-resolution/send`);
      toast.success(`Pedido enviado ao Hospital (${r.data.items} itens).`);
      refresh();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const unblock = async () => {
    const unblocked_description = window.prompt('Justificação do desbloqueio:');
    if (!unblocked_description) return;
    setBusy(true);
    try {
      await api.post(`/orders/${id}/unblock`, { unblocked_description });
      toast.success('Encomenda desbloqueada.');
      refresh();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancelar encomenda?')) return;
    setBusy(true);
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.success('Encomenda cancelada.');
      refresh();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gencrm_order_editor">
      {/* Header */}
      <div className="gencrm_order_editor__header">
        <h2>Encomenda #{order.order_number} <OrderStatusBadge statusId={order.status_id} /></h2>
        <div className="gencrm_order_editor__header-actions">
          <LockBadge orderId={id} lock={lock} />
        </div>
      </div>

      {order.sap_order_number && (
        <div className="gencrm_alert gencrm_alert--info">
          SAP VBELN: <code>{order.sap_order_number}</code> · Enviado em {date(order.sap_sent_at)}
          {order.sap_last_check && ` · Último check ${date(order.sap_last_check)}`}
        </div>
      )}

      {order.block_reason_name && (
        <div className="gencrm_alert gencrm_alert--warning">
          🚫 Bloqueada por motivo: <strong>{order.block_reason_name}</strong> — {order.block_description}
        </div>
      )}

      {isLockedByOther && (
        <div className="gencrm_alert gencrm_alert--error">
          ⚠️ Editável apenas em modo leitura — locked por <strong>{lock.holder}</strong>.
        </div>
      )}

      {/* Ações condicionais ao estado */}
      <div className="gencrm_order_editor__actions">
        {order.status_id === STATES.DRAFT && (
          <>
            <button className="gencrm_btn" onClick={() => setBlockModal(true)} disabled={busy}>🚫 Bloquear</button>
            <button className="gencrm_btn" onClick={requestPriceResolution} disabled={busy}>
              💶 Resolver Preços
            </button>
            <button className="gencrm_btn gencrm_btn--primary" onClick={sendToSap} disabled={busy}>
              📤 Enviar SAP
            </button>
            <button className="gencrm_btn gencrm_btn--danger" onClick={cancel} disabled={busy}>✗ Cancelar</button>
          </>
        )}
        {order.status_id === STATES.BLOCKED && (
          <button className="gencrm_btn" onClick={unblock} disabled={busy}>🔓 Desbloquear</button>
        )}
        {order.status_id === STATES.AVAILABLE && (
          <>
            <button className="gencrm_btn gencrm_btn--primary" onClick={sendToSap} disabled={busy}>📤 Enviar SAP</button>
            <button className="gencrm_btn" onClick={cancel} disabled={busy}>✗ Cancelar</button>
          </>
        )}
        {order.status_id === STATES.DELIVERED && (
          <>
            <button className="gencrm_btn gencrm_btn--primary" onClick={checkSapStatus} disabled={busy}>
              🔄 Verificar Status SAP
            </button>
            <button className="gencrm_btn" onClick={() => api.post(`/orders/${id}/sap/resend`).then(refresh)} disabled={busy}>
              🔁 Reenviar Pending
            </button>
          </>
        )}
        {order.status_id === STATES.RELEASED_BY_HOSPITALAR && (
          <>
            <button className="gencrm_btn gencrm_btn--primary" onClick={sendToSap} disabled={busy}>📤 Enviar SAP</button>
            <button className="gencrm_btn" onClick={cancel} disabled={busy}>✗ Cancelar</button>
          </>
        )}
        {order.status_id === STATES.NEEDS_CLIENT_REVIEW && (
          <>
            <button className="gencrm_btn gencrm_btn--primary" onClick={sendToSap} disabled={busy}>📤 Enviar SAP</button>
            <button className="gencrm_btn" onClick={cancel} disabled={busy}>✗ Cancelar</button>
          </>
        )}
      </div>

      {/* Customer */}
      <div className="gencrm_card">
        <h3>Cliente</h3>
        <div className="gencrm_card__grid">
          <div><strong>Cliente:</strong> {order.customer_name || '—'}</div>
          <div><strong>Customer ID:</strong> <code>{order.customer_id}</code></div>
          <div><strong>Receiver:</strong> {order.receiver_id || '—'}</div>
          <div><strong>Criada por:</strong> {order.user_name || '—'} em {date(order.created_at)}</div>
        </div>
      </div>

      {/* Detalhes */}
      <div className="gencrm_card">
        <h3>Detalhes</h3>
        <div className="gencrm_card__grid">
          <div><strong>Tipo:</strong> <code>{order.order_type}</code></div>
          <div><strong>Nº Cliente:</strong> {order.client_order_number || '—'}</div>
          <div><strong>Compromisso:</strong> {order.compromiso_number || '—'}</div>
          <div><strong>Cabimento:</strong> {order.cabimento_number || '—'}</div>
          <div><strong>Data pedido:</strong> {date(order.order_date)}</div>
          <div><strong>Data entrega:</strong> {date(order.delivery_date)}</div>
          <div><strong>Data fixação:</strong> {date(order.price_fixation_date)}</div>
          <div><strong>Transporte:</strong> {order.transport_method || '—'}</div>
          <div><strong>Anexo VII:</strong> {order.attachment_vii_numbers || '—'}</div>
          {order.invoice_comments && <div><strong>Obs. fatura:</strong> {order.invoice_comments}</div>}
        </div>
      </div>

      {/* Linhas */}
      <div className="gencrm_card">
        <h3>Linhas ({order.lines?.length || 0})</h3>
        <DataTable
          columns={columns}
          data={order.lines || []}
          rowKey={(l) => l.id}
          rowClassName={(l) => l.cancelled_at ? 'is-cancelled' : ''}
          pageSize={50}
          density="compact"
          enableTotals
          emptyText="Sem linhas."
          exportPrefix="gencrm_order_lines"
          layoutConfig={layoutConfig}
          onLayoutChange={saveLayout}
        />
      </div>

      {blockModal && (
        <BlockOrderModal
          orderId={id}
          onClose={() => setBlockModal(false)}
          onBlocked={() => { setBlockModal(false); refresh(); }}
        />
      )}
    </div>
  );
};

export default OrderEditor;
